import {config} from 'config/env';
import {SubDistrictStatusType} from 'constants/index';
import {getStoreAreasV2} from 'services/location/location-service';
import {getStoreAreas} from 'services/user/user-service';
import {CurrentStoreType} from 'store/types/app';
import {Address, AddressAreaLocale, CreateAddressInputType, StoreAreas, UserAddress} from 'types';

interface IFilterStoreArea {
  data: StoreAreas[];
  z: string[];
  pIDs: string[];
  dIDs: string[];
  sIDs: string[];
}

export const getStringFormOfAddress = (address: UserAddress, isThai = false, moveZipToLast = false) => {
  return [
    !moveZipToLast ? address?.zip : undefined,
    address?.company,
    address?.address1,
    address?.address2,
    address?.subDistrict?.[isThai ? 'nameTh' : 'nameEn'],
    address?.district?.[isThai ? 'nameTh' : 'nameEn'],
    address?.province?.[isThai ? 'nameTh' : 'nameEn'],
    moveZipToLast ? address?.zip : undefined
  ]
    .filter(Boolean)
    .join(', ');
};

export function mapUserAddressToShippingAddress(
  address: UserAddress,
  isThai: boolean,
  isMapEnhancementEnabled?: boolean
): Address {
  const {__typename: _a, ...provinceWithLocale} = address.province;
  const {__typename: _b, ...districtWithLocale} = address.district;
  const {__typename: _c, ...subDistrictWithLocale} = address.subDistrict as AddressAreaLocale;

  const convertedAddress = {
    id: address?.id,
    address1: address?.address1,
    address2: address.address2,
    province: isThai ? address.province?.nameTh : address.province?.nameEn,
    provinceCode: address.province?.id,
    city: isThai ? address.district?.nameTh : address.district?.nameEn,
    company: address.company,
    country: address.country,
    countryCode: '',
    firstName: address.firstName,
    lastName: address.lastName,
    phone: address.mobile,
    zip: address.zip,
    subdistrict: isThai ? address.subDistrict?.nameTh : address?.subDistrict?.nameEn || '',
    latitude: address.latitude || 0,
    longitude: address.longitude || 0,
    taxId: address?.taxId || '',
    branchId: address?.branchId || '',
    ...(config.ENABLE_ADDRESS_LOCALE_IN_CREATE_CHECKOUT
      ? {
          provinceWithLocale,
          districtWithLocale,
          subDistrictWithLocale
        }
      : {})
  };

  if (!isMapEnhancementEnabled) {
    return convertedAddress;
  } else {
    return {
      ...convertedAddress,
      userProvidedLatitude:
        (config.IS_ENABLE_LOCUS_LAT_LONG ? address?.locus_latitude || address.user_latitude : address.user_latitude) ||
        undefined,
      userProvidedLongitude:
        (config.IS_ENABLE_LOCUS_LAT_LONG
          ? address?.locus_longitude || address.user_longitude
          : address.user_longitude) || undefined
    };
  }
}

export const getAllKysInFilterArea = (data: StoreAreas[], k: keyof StoreAreas) =>
  Array.from(new Set(data?.map((i) => i[k])).keys());

export const filterStoreArea = (storeArea: StoreAreas[], filter?: Partial<StoreAreas>): IFilterStoreArea => {
  const data = storeArea.filter((i) => {
    return filter
      ? Object.entries(filter).reduce((acc, [k, v]) => {
          if (v && i[k as keyof StoreAreas] !== v) {
            acc = false;
          }
          return acc;
        }, true)
      : i;
  });
  return {
    data,
    z: getAllKysInFilterArea(data, 'z'),
    pIDs: getAllKysInFilterArea(data, 'pID'),
    dIDs: getAllKysInFilterArea(data, 'dID'),
    sIDs: getAllKysInFilterArea(data, 'sID')
  };
};

export const getMetaData = async (zip?: string) => {
  let data: StoreAreas[] = [];
  try {
    if (config.IS_LOCATION_SVC_ENABLED && zip) {
      data = await getStoreAreasV2(zip);
    } else {
      const response = await getStoreAreas({
        // ['status']: StatusEnum.DELIVERABLE
      });
      data = response.data.getStoreAreas || [];
    }
  } catch (error: any) {
    console.log('error', error.message);
  }
  return data;
};

export const assignDefaults = async ({
  val: _v,
  curr,
  isThai,
  locationsV2
}: {
  val: Partial<CreateAddressInputType>;
  curr?: CurrentStoreType;
  isThai?: boolean;
  locationsV2?: StoreAreas[];
}) => {
  const data = config.IS_LOCATION_SVC_ENABLED
    ? await getMetaData(_v.zip || (curr?.zipcode as string))
    : locationsV2?.length
      ? locationsV2
      : await getMetaData();
  const v = {
    ..._v
  };
  const hasVal = v.zip && (v.province_id || v.district_id || v.subDistrict_id);
  const getFilter = (_isThai?: boolean) => {
    const _filters = {} as StoreAreas;
    if (hasVal) {
      v.zip && (_filters.z = v.zip);
      v.province_id && (_filters.pID = v.province_id);
      v.district_id && (_filters.dID = v.district_id);
      v.subDistrict_id && (_filters.sID = v.subDistrict_id);
    } else {
      curr?.zipcode && (_filters.z = curr?.zipcode);
      curr?.subdistrict && (_filters[_isThai ? 's' : 'se'] = curr?.subdistrict);
    }
    return _filters;
  };
  let filters = getFilter(isThai);
  let store = filterStoreArea(data, filters).data?.[0];
  if (!store) {
    filters = getFilter(!isThai);
    store = filterStoreArea(data, filters).data?.[0];
  }
  if (store) {
    v.zip || (v.zip = store.z);
    v.province_id || (v.province_id = store.pID);
    v.provinceName || (v.provinceName = store[isThai ? 'p' : 'pe']);
    v.subDistrict_id || (v.subDistrict_id = store.sID);
    v.subDistrictName || (v.subDistrictName = store[isThai ? 's' : 'se']);
    v.district_id || (v.district_id = store.dID);
    v.districtName || (v.districtName = store[isThai ? 'd' : 'de']);
  }
  return {
    v,
    data,
    store
  };
};

export const trimValues = (values: CreateAddressInputType): CreateAddressInputType => {
  const trimmedValues: CreateAddressInputType = {...values};

  (Object.keys(values) as (keyof CreateAddressInputType)[]).forEach((key) => {
    const value = values[key];
    if (typeof value === 'string' && value) {
      (trimmedValues[key] as unknown as string) = value.trim();
    }
  });

  return trimmedValues;
};

export const getAddressString = (
  storeArea: StoreAreas[],
  province_id: string,
  district_id: string,
  subDistrict_id?: string,
  zip?: string | number,
  address1?: string,
  address2?: string
) => {
  if (province_id && district_id) {
    const province = storeArea.find(({pID}) => pID === province_id);
    const subDistrict = storeArea.find(({sID}) => sID === subDistrict_id);
    const district = storeArea.find(({dID}) => dID === district_id);
    const _addressRegion = `${subDistrict ? `${subDistrict.se}, ` : ''}${district ? `${district.de}, ` : ''}${
      province ? `${province.pe}, ` : ''
    }${zip ? `${zip}` : ''}`;

    return `${address1 ? `${address1}, ` : ''}${address2 ? `${address2}, ` : ''}${_addressRegion}`;
  }
};

export const setDefaults = ({pIDs, dIDs, sIDs}: Partial<IFilterStoreArea>, setFieldValue: any) => {
  pIDs && setFieldValue('province_id', pIDs.length === 1 ? pIDs[0] : null);
  dIDs && setFieldValue('district_id', dIDs.length === 1 ? dIDs[0] : null);
  sIDs && setFieldValue('subDistrict_id', sIDs.length === 1 ? sIDs[0] : null);
};

export const findAvailableIds = (data: IFilterStoreArea) => {
  const availableData = data.data.filter((elm) => elm.status === SubDistrictStatusType.DELIVERABLE);
  if (availableData.length) {
    const {pID, dID, sID} = availableData[0];
    return {
      pIDs: [pID],
      dIDs: [dID],
      sIDs: [sID]
    };
  }
  return data;
};

export const projectFilteredArea = (filteredStoreArea: StoreAreas[], projection: keyof StoreAreas) => {
  return Object.values(
    filteredStoreArea.reduce((acc, i) => {
      acc[i[projection]] = i;
      return acc;
    }, {} as any)
  ) as StoreAreas[];
};
